import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../../lib/LangContext";
import { CITIES, CONTACT_INFO } from "../../lib/translations";
import { splitPath, urlToLang } from "../../lib/i18nRoutes";
import {
  SITE_URL, OG_IMAGE, SEO_LANGS, SEO_PAGES, SEO_TEXT, pageKeyFromRest, pageMeta, absUrl, xDefaultUrl,
} from "../../lib/seoPages";

const JSONLD_ID = "puregloss-jsonld";
const LEGACY_PATHS = new Set(Object.values(SEO_PAGES));
// Tags owned by PUREGARAGE /app that must not leak onto PUREGLOSS pages.
const clearForeign = () => {
  document.getElementById("puregarage-jsonld")?.remove();
  document.head.querySelector('meta[property="og:image:alt"]')?.remove();
};
const PRIVATE_RE = /^\/(admin|admin-leads|admin-reset|showcase|academy\/login|academy\/dashboard)(\/|$)/;

const upsert = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};
const meta = (name, content) =>
  upsert(`meta[name="${name}"]`, () => Object.assign(document.createElement("meta"), { name }), { content });
const prop = (property, content) =>
  upsert(`meta[property="${property}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", property); return m; }, { content });
const clearLinks = () => document.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((l) => l.remove());
const addLink = (attrs) => {
  const l = document.createElement("link");
  Object.entries(attrs).forEach(([k, v]) => l.setAttribute(k, v));
  document.head.appendChild(l);
};

const businessJsonLd = (description) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "PUREGLOSS — Detailing & Sofa Cleaning",
  description,
  image: OG_IMAGE,
  url: SITE_URL,
  telephone: CONTACT_INFO.phoneE164,
  email: CONTACT_INFO.email,
  priceRange: "€€",
  address: { "@type": "PostalAddress", addressLocality: "Euskirchen", addressRegion: "Nordrhein-Westfalen", addressCountry: "DE" },
  areaServed: CITIES.map((c) => ({ "@type": "City", name: c })),
  sameAs: [CONTACT_INFO.instagramUrl, CONTACT_INFO.telegramUrl],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "08:00",
    closes: "20:00",
  },
});

const setJsonLd = (data) => {
  let script = document.getElementById(JSONLD_ID);
  if (!data) { if (script) script.remove(); return; }
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = JSONLD_ID;
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

const applyPublic = (lang, key, t) => {
  clearForeign();
  const { title, description } = pageMeta(lang, key, t.footer || {});
  const urlCode = SEO_LANGS.find((l) => l.lang === lang)?.url || "de";
  const path = SEO_PAGES[key];
  const canonical = absUrl(urlCode, path);
  document.title = title;
  meta("description", description);
  meta("robots", "index, follow");
  prop("og:type", "website");
  prop("og:site_name", "PUREGLOSS");
  prop("og:title", title);
  prop("og:description", description);
  prop("og:url", canonical);
  prop("og:image", OG_IMAGE);
  prop("og:locale", SEO_LANGS.find((l) => l.lang === lang)?.og || "de_DE");
  meta("twitter:card", "summary_large_image");
  meta("twitter:title", title);
  meta("twitter:description", description);
  meta("twitter:image", OG_IMAGE);
  clearLinks();
  addLink({ rel: "canonical", href: canonical });
  SEO_LANGS.forEach((l) => addLink({ rel: "alternate", hreflang: l.html, href: absUrl(l.url, path) }));
  addLink({ rel: "alternate", hreflang: "x-default", href: xDefaultUrl(path) });
  setJsonLd(key === "home" ? businessJsonLd(description) : null);
};

const applyNoIndex = (title) => {
  clearForeign();
  if (title) document.title = title;
  meta("robots", "noindex, nofollow");
  clearLinks();
  setJsonLd(null);
};

export const SEOHead = () => {
  const { lang, t } = useLang();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname === "/app" || pathname.startsWith("/app/")) {
      meta("robots", "index, follow");
      clearLinks();
      setJsonLd(null);
      return;
    }
    if (PRIVATE_RE.test(pathname)) {
      applyNoIndex("PUREGLOSS");
      return;
    }
    const { urlLang, rest } = splitPath(pathname);
    if (!urlLang) {
      // Legacy URLs redirect to their localized twin; anything else is a 404.
      if (!LEGACY_PATHS.has(pathname.replace(/\/+$/, "") || "/")) {
        applyNoIndex((SEO_TEXT[lang] || SEO_TEXT.de).notFound.title);
      }
      return;
    }
    const key = pageKeyFromRest(rest);
    const pageLang = urlToLang(urlLang);
    if (key) applyPublic(pageLang, key, t);
    else applyNoIndex((SEO_TEXT[pageLang] || SEO_TEXT.de).notFound.title);
  }, [pathname, lang, t]);

  return null;
};
